import { useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthContext';
import Sidebar from '../../dashboard/Sidebar';
import dash from '../../dashboard/DashboardPage.module.css';
import { initials, teamShortName } from '../../liveSession/lib/formatters';
import { useMyTeam } from '../../team/useMyTeam';
import styles from './PlaybookShell.module.css';

type Props = {
  crumb: string;
  wide?: boolean;
  children: ReactNode;
};

export default function PlaybookShell({ crumb, wide = false, children }: Props) {
  const { user, logout } = useAuth();
  const { team } = useMyTeam(user?.id);
  const [signingOut, setSigningOut] = useState(false);

  const userInitials = user ? initials(user.displayName || user.email) : '??';
  const teamName = team?.name?.trim() ? team.name : null;

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className={dash.app}>
      <Sidebar />
      <div className={dash.main}>
        <header className={dash.topbar}>
          {team && (
            <>
              <div className={dash.teamCtx}>
                <span className={dash.teamCtxLogo}>{teamShortName(teamName)}</span>
                <div className={dash.teamCtxInfo}>
                  <div className={dash.teamCtxLabel}>Active Team</div>
                  <div className={dash.teamCtxName}>{teamName ?? 'Unnamed team'}</div>
                </div>
              </div>
              <div className={dash.topbarDivider} aria-hidden="true" />
            </>
          )}
          <nav className={dash.breadcrumb} aria-label="Breadcrumb">
            <span>Stratos</span>
            <span className={dash.sep}>/</span>
            <span className={dash.here}>{crumb}</span>
          </nav>
          <div className={dash.spacer} />
          {user && (
            <div className={dash.user}>
              <span className={dash.avatar} aria-hidden="true">{userInitials}</span>
              <div>
                <div className={dash.userName}>{user.displayName}</div>
                <div className={dash.userEmail}>{user.email}</div>
              </div>
            </div>
          )}
          <button type="button" className={dash.signOut} onClick={handleSignOut} disabled={signingOut}>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </header>
        <div className={wide ? `${styles.content} ${styles.wide}` : styles.content}>{children}</div>
      </div>
    </div>
  );
}
